import React, { useState, useRef } from "react";
import Icon from "../AppIcon";

/**
 * Reusable CSV Import Modal.
 *
 * Props:
 *  - isOpen      {boolean}   Whether the modal is visible
 *  - onClose       {function}  Called when the user cancels / closes
 *  - onImport  {function}  Called with the selected File – should return a promise
 *  - title         {string} Modal heading (e.g. "Import Job Roles")
 *  - description   {string}    Short helper text shown below the heading
 *  - columns     {Array<{ name, required, description }>}  Expected CSV columns
 *- sampleRows    {Array<Array<string>>}  Optional sample data rows for the template
 */
const CsvImportModal = ({
  isOpen,
  onClose,
  onImport,
  title = "Import from CSV",
  description,
  columns = [],
  sampleRows = [],
}) => {
const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
    setFile(selected);
      setResult(null);
    setError(null);
    }
  };

  const handleDrop = (e) => {
  e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped && dropped.name.endsWith(".csv")) {
  setFile(dropped);
      setResult(null);
      setError(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const response = await onImport(file);
    const data = response?.data ?? response?.Data ?? response;
      setResult(data);
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const headers = columns.map((c) => c.name);
    const rows = sampleRows.length
      ? sampleRows
      : [columns.map((c) => c.sample || "")];

    const escape = (v) => {
      const s = (v ?? "").toString();
      return s.includes(",") || s.includes('"')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
};

    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "-").toLowerCase()}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError(null);
    onClose();
  };

  const requiredColumns = columns.filter((c) => c.required);
  const optionalColumns = columns.filter((c) => !c.required);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[1200]" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-[1201] flex items-center justify-center p-4">
        <div
    className="bg-card rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
    >
    {/* Header */}
     <div className="flex items-center justify-between p-6 border-b border-border">
    <div className="flex items-center gap-3">
           <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
   <Icon name="Upload" size={20} className="text-primary" />
           </div>
 <div>
  <h2 className="text-lg font-heading font-semibold">{title}</h2>
                {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
    )}
   </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-muted rounded-md">
              <Icon name="X" size={20} />
   </button>
     </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto space-y-5">
            {/* Column spec */}
            {columns.length > 0 && (
       <div>
        <h3 className="text-sm font-semibold mb-2">Expected CSV Columns</h3>

       {requiredColumns.length > 0 && (
    <div className="mb-3">
          <p className="text-xs font-semibold text-error mb-1 flex items-center gap-1">
    <Icon name="AlertCircle" size={12} /> Required
       </p>
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
  {requiredColumns.map((col) => (
           <div
   key={col.name}
   className="text-xs px-2.5 py-1.5 bg-error/5 border border-error/20 rounded-md"
      >
        <span className="font-semibold text-foreground">{col.name}</span>
  {col.description && (
  <span className="text-muted-foreground ml-1">— {col.description}</span>
         )}
              </div>
  ))}
           </div>
      </div>
  )}

     {optionalColumns.length > 0 && (
  <div>
        <p className="text-xs font-semibold text-muted-foreground mb-1">Optional</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
  {optionalColumns.map((col) => (
       <div
key={col.name}
 className="text-xs px-2.5 py-1.5 bg-muted/50 border border-border rounded-md"
  >
          <span className="font-semibold text-foreground">{col.name}</span>
    {col.description && (
       <span className="text-muted-foreground ml-1">— {col.description}</span>
        )}
   </div>
   ))}
         </div>
        </div>
     )}

          <button
        onClick={handleDownloadTemplate}
          className="btn btn-outline btn-sm mt-3"
         >
       <Icon name="Download" size={14} />
            Download Template CSV
                </button>
              </div>
            )}

            {/* Matching rules info */}
      <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg text-xs text-muted-foreground flex items-start gap-2">
<Icon name="Info" size={14} className="text-primary mt-0.5 shrink-0" />
     <span>
                Required fields are matched by name. If a required value cannot be
                matched to an existing record, that row will be skipped. Check the
   results summary below after importing.
        </span>
         </div>

    {/* Dropzone */}
          <div
         className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
    file
      ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/50"
            }`}
  onDragOver={(e) => e.preventDefault()}
    onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
  role="button"
        tabIndex={0}
       >
              <input
         ref={fileInputRef}
       type="file"
  accept=".csv"
     className="hidden"
                onChange={handleFileChange}
  />
{file ? (
        <div className="flex items-center justify-center gap-3">
    <Icon name="FileText" size={24} className="text-primary" />
    <div className="text-left">
    <p className="font-semibold text-sm">{file.name}</p>
          <p className="text-xs text-muted-foreground">
       {(file.size / 1024).toFixed(1)} KB — Click or drop to change
     </p>
 </div>
   </div>
           ) : (
         <>
      <Icon
     name="Upload"
             size={32}
            className="mx-auto mb-2 text-muted-foreground"
       />
       <p className="text-sm font-medium">
       Drop a CSV file here, or click to browse
 </p>
      <p className="text-xs text-muted-foreground mt-1">Only .csv files</p>
  </>
    )}
     </div>

     {/* Error */}
            {error && (
              <div className="alert alert-error">
            <Icon name="AlertCircle" size={18} />
          <span>{error}</span>
         </div>
            )}

      {/* Result */}
        {result && (
              <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3 text-center">
         <div className="card p-3">
        <p className="text-xs text-muted-foreground">Total Rows</p>
           <p className="text-xl font-bold">{result.totalRows ?? 0}</p>
     </div>
       <div className="card p-3">
        <p className="text-xs text-muted-foreground">Imported</p>
          <p className="text-xl font-bold text-success">
      {result.importedCount ?? 0}
         </p>
                  </div>
   <div className="card p-3">
            <p className="text-xs text-muted-foreground">Skipped</p>
     <p className="text-xl font-bold text-warning">
    {result.skippedCount ?? 0}
        </p>
 </div>
    </div>

   {result.errors?.length > 0 && (
  <div className="max-h-40 overflow-y-auto border border-border rounded-lg">
     <table className="w-full text-xs">
      <thead className="bg-muted/50 sticky top-0">
          <tr>
              <th className="text-left px-3 py-2 font-semibold">Row</th>
            <th className="text-left px-3 py-2 font-semibold">Issue</th>
      </tr>
  </thead>
          <tbody className="divide-y divide-border">
      {result.errors.map((err, i) => (
            <tr key={i}>
    <td className="px-3 py-1.5 text-muted-foreground">{err.row}</td>
             <td className="px-3 py-1.5">{err.message}</td>
    </tr>
 ))}
      </tbody>
</table>
 </div>
      )}
        </div>
          )}
          </div>

  {/* Footer */}
   <div className="flex justify-end gap-3 p-6 border-t border-border">
          <button className="btn btn-outline" onClick={handleClose}>
  {result ? "Close" : "Cancel"}
            </button>
            {!result && (
     <button
      className="btn btn-primary"
        onClick={handleImport}
     disabled={!file || importing}
  >
    {importing ? (
 <>
           <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
           Importing...
          </>
            ) : (
           <>
    <Icon name="Upload" size={16} />
              Import
                  </>
        )}
              </button>
     )}
          </div>
    </div>
      </div>
    </>
  );
};

export default CsvImportModal;
